import { PM_CHAT_HISTORY_LIMIT, type TaskContext } from "@project/shared";

export { PM_CHAT_HISTORY_LIMIT };

/**
 * The diff command every prompt hands an agent for "what did this branch
 * change".
 *
 * Two-dot `git diff <base>..HEAD` compares against the LOCAL `<base>` ref,
 * which in a pod's single-branch clone is either absent or stale — the agent
 * then reviews a diff that includes commits already merged into the base. The
 * merge-base form fetches the base first and diffs from the true fork point,
 * which is what the repo's own guidance prescribes.
 *
 * Commit-count forms like `HEAD~3` fail the same way and are worse, because
 * they look precise: `HEAD~N` walks first-parent ancestry straight through the
 * base's own merge commits, so on a branch with fewer than N commits it
 * silently presents the base's work as the agent's. Always express "what this
 * branch changed" as a merge-base range, never as a commit count.
 */
export function baseDiffCommand(baseBranch?: string | null, flags?: string): string {
  const base = baseBranch ?? "dev";
  const suffix = flags ? ` ${flags}` : "";
  return `git fetch origin ${base} -q && git diff $(git merge-base origin/${base} HEAD)..HEAD${suffix}`;
}

/**
 * What a gate result actually means.
 *
 * Prompts used to name the gate commands and stop there, which left three
 * silent-failure modes to be rediscovered per session: a second gate evicting
 * the first (`scripts/singleton.sh` gives the heavy gates one lock on a pod),
 * a run that never reached vitest, and `--affected` selecting nothing for a
 * package that was in fact changed. All three read as success.
 *
 * Every remedy here is deliberately scoped so it cannot become a second full
 * pass. In particular, the missing-summary bullet used to say "no vitest
 * summary → unverified, re-run it", which was simply wrong and cost a whole
 * redundant gate whenever it fired: `turbo.json` sets `outputLogs:
 * "errors-only"`, so a PASSING gate prints no per-suite summary at all. The
 * captured exit code is the authority.
 */
export function gateFailureModes(): string[] {
  return [
    `Reading a gate result correctly is what keeps this to ONE pass:`,
    `- Capture the exit code explicitly (\`<gate> > <log> 2>&1; echo "EXIT:$?" >> <log>\`) — piping a gate through \`tail\` masks it. That \`EXIT:\` line, plus turbo's final \`Tasks:\` count, is the authority on pass/fail.`,
    `- A green gate that printed no per-suite summary (no \`Test Files\`/\`Tests\` line) is still green: \`turbo.json\` sets \`outputLogs: "errors-only"\`, so a PASSING run prints nothing per suite. Do NOT re-run a clean-exit gate to "see the counts" — if you genuinely need them, run one package's script directly (\`bun run --cwd <pkg> test\`).`,
    `- Exit 143, or \`singleton: stopping running '<label>'\` on stderr, means a second gate you started evicted this one (on a pod the heavy gates share one lock). 143 is never a pass — but an evicted run never finished, so re-running it alone is still your one pass, not a repeat. Run one gate at a time; \`bun run check\` is not lock-wrapped, so it is safe alongside a test run.`,
    `- Confirm every package your diff touches actually appears in the run. \`--affected\` can select nothing for a package you changed; when that happens run just that package's suite directly, e.g. \`bun run --cwd apps/api test:unit <changed test files>\` — not the whole gate again.`,
    ...gateWaitProtocol(),
  ];
}

/**
 * The single largest pure-waste category in the 2026-07-27 fleet audit: 73
 * log-peek calls in five sessions, one session arming FIVE redundant waiters on
 * one log before reaching Monitor, and another launching + killing a
 * byte-identical command four times over cwd anxiety.
 */
export function gateWaitProtocol(): string[] {
  return [
    `How to wait for a gate — one gate, one wait, no polling:`,
    `- Launch it ONCE with \`run_in_background: true\`, then END YOUR TURN. The completion notification re-invokes you. Do not read the output file, \`tail\`/\`wc\`/\`pgrep\` it, or start a second watcher on a log another run already owns — a quiet gate is still running.`,
    `- Never wrap a gate in a foreground \`timeout\`: killing your own run at 590s produces exit 124/143 and ZERO information, and you then have to run it again unbounded. \`sleep N; <cmd>\` is hard-blocked for the same reason.`,
    `- Never relaunch a command you just killed without changing something. State cwd explicitly in the command (\`cd /workspaces/repo && …\`, \`--root\`, \`git -C\`) rather than trusting the shell's working directory.`,
    `- The completion notification reports the WRAPPER's exit code, not the gate's. A notification saying "exit code 0" over a log containing \`EXIT:1\` is a failed gate. The \`EXIT:\` line in the log is the authority.`,
    `- Scope heavy gates to what you changed (\`--filter=<pkg>\`). An unscoped \`check:affected\` can pull a large web typecheck into scope for a diff touching no web files and get OOM-killed (exit 137) after minutes, where the scoped run takes seconds.`,
    `- Merge the base BEFORE the gate pass, never after (see the Pre-PR Protocol). If that merge touched \`package.json\`/\`bun.lock\`, run \`bun install\` before starting the gate — a changed lockfile makes gates fail for reasons unrelated to your diff.`,
  ];
}

/**
 * 4 of 5 audited sessions treated a CI red as their own bug and spent 6–12
 * calls each proving otherwise. Front-load the four checks that distinguish
 * "my diff broke it" from "this was already broken".
 */
export function ciTriageChecklist(baseBranch?: string): string[] {
  const base = baseBranch?.trim() || "dev";
  return [
    `Before treating a CI failure as yours, run these four checks first — most reported reds are not caused by the diff:`,
    `1. Is the failing run's head SHA still current? A red run against a superseded commit is stale.`,
    `2. Is the only \`##[error]\` a cancellation ("The operation was canceled", "The runner has received a shutdown signal")? That is a reclaimed/preempted runner, not a code failure — re-run it.`,
    `3. Is the failing file even in your diff? \`git diff origin/${base}...HEAD --stat -- <path>\` — empty output means you did not touch it. Check whether \`origin/${base}\` already fails the same way before investigating further.`,
    `4. Is \`origin/${base}\` itself green? A semantic merge conflict on the base branch fails every open PR at once.`,
    `Known environmental failures — recognize, do not re-diagnose: suites needing a real Elasticsearch fail without \`RC_TEST_ES_URL\` and cannot pass in a pod; exit 137 is a pod OOM, not a code error; \`gh\` returning \`HTTP 401: Bad credentials\` means the pod token aged out (~1h) — re-exporting it does not help, so use the Conveyor MCP CI-status tools instead of spending calls re-testing \`gh\`.`,
    `A Dependabot PR showing "no checks reported" is in \`action_required\` — a maintainer must approve the workflow run. That is not "CI did not run", and you cannot unblock it yourself.`,
  ];
}

export function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined) return "";
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export interface FileAttachment {
  fileName: string;
  mimeType: string;
  fileSize?: number;
  content?: string;
  contentEncoding?: string;
  downloadUrl?: string;
  fileId?: string;
}

export function formatChatFile(file: FileAttachment): string[] {
  const sizeStr = file.fileSize ? `, ${formatFileSize(file.fileSize)}` : "";
  if (file.content && file.contentEncoding === "utf-8") {
    return [
      `[Attached: ${file.fileName} (${file.mimeType}${sizeStr})]`,
      "```",
      file.content,
      "```",
    ];
  }
  if (!file.content) {
    return [`[Attached: ${file.fileName} (${file.mimeType}${sizeStr})]: ${file.downloadUrl}`];
  }
  if (file.content && file.contentEncoding === "base64") {
    const link = file.downloadUrl ? ` or download: ${file.downloadUrl}` : "";
    return [
      `[Attached image: ${file.fileName} (${file.mimeType}${sizeStr}) — use get_attachment("${file.fileId}") to view${link}]`,
    ];
  }
  return [`[Attached: ${file.fileName} (${file.mimeType}${sizeStr})]`];
}

export function formatTaskFile(file: FileAttachment): string[] {
  if (file.content && file.contentEncoding === "utf-8") {
    return [`\n### ${file.fileName} (${file.mimeType})`, "```", file.content, "```"];
  }
  if (file.content && file.contentEncoding === "base64") {
    const size = formatFileSize(file.fileSize);
    const link = file.downloadUrl ? ` or download: ${file.downloadUrl}` : "";
    return [
      `- [Attached image: ${file.fileName} (${file.mimeType}${size ? `, ${size}` : ""}) — use get_attachment("${file.fileId}") to view${link}]`,
    ];
  }
  if (!file.content) {
    return [`- **${file.fileName}** (${file.mimeType}): ${file.downloadUrl}`];
  }
  return [];
}

export function formatChatHistory(
  chatHistory: TaskContext["chatHistory"],
  limit?: number,
): string[] {
  const relevant = chatHistory.slice(-(limit ?? PM_CHAT_HISTORY_LIMIT));
  const parts = [`\n## Recent Chat Context`];
  for (const msg of relevant) {
    const sender = msg.userName ?? msg.role;
    parts.push(`[${sender}]: ${msg.content}`);
    if (msg.files?.length) {
      for (const file of msg.files) {
        parts.push(...formatChatFile(file));
      }
    }
  }
  return parts;
}

export function formatRepoRefs(repoRefs: NonNullable<TaskContext["repoRefs"]>): string[] {
  const parts: string[] = [];
  parts.push(`\n## Repository References`);
  for (const ref of repoRefs) {
    const icon = ref.refType === "folder" ? "folder" : "file";
    parts.push(`- [${icon}] \`${ref.path}\``);
  }
  return parts;
}

export function formatReferenceProjects(
  referenceProjects: NonNullable<TaskContext["referenceProjects"]>,
): string[] {
  const parts: string[] = [];
  parts.push(`\n## Reference Projects`);
  parts.push(
    `These sibling Conveyor projects have been shallow-cloned read-only into \`/workspaces/references/<slug>/\` for inspiration. You MAY grep/read them to compare approaches, but you MUST NOT modify them or commit anything from them into this task's repo.\n`,
  );
  for (const ref of referenceProjects) {
    const repo =
      ref.githubRepoOwner && ref.githubRepoName
        ? ` (${ref.githubRepoOwner}/${ref.githubRepoName})`
        : "";
    parts.push(`- **${ref.name}**${repo} — \`/workspaces/references/${ref.slug}/\``);
  }
  return parts;
}

export function formatProjectObjectives(
  objectives: NonNullable<TaskContext["projectObjectives"]>,
): string[] {
  const parts: string[] = [];
  parts.push(`\n## Project Objectives`);
  for (const obj of objectives) {
    const dates = `${obj.startDate.split("T")[0]} to ${obj.endDate.split("T")[0]}`;
    parts.push(`- **${obj.name}** (${dates})${obj.description ? ": " + obj.description : ""}`);
  }
  return parts;
}

export function formatRecentRelatedTasks(
  tasks: NonNullable<TaskContext["recentRelatedTasks"]>,
): string[] {
  const parts: string[] = [];
  parts.push(`\n## Recently Completed Related Tasks`);
  parts.push(
    `These tasks in the same domain were recently completed. Use them for context on recent changes and patterns.\n`,
  );
  for (const task of tasks) {
    const tags = task.tagNames.length > 0 ? ` [${task.tagNames.join(", ")}]` : "";
    const pr = task.githubPRUrl ? ` — PR: ${task.githubPRUrl}` : "";
    parts.push(`- **${task.title}**${tags}${pr}`);
  }
  return parts;
}

export function formatIncidents(incidents: NonNullable<TaskContext["incidents"]>): string[] {
  const parts: string[] = [];
  parts.push(`\n## Linked Incidents`);
  parts.push(
    `This task has linked incidents. Review them for context on the problem being addressed.\n`,
  );
  for (const inc of incidents) {
    const severity = inc.severity ? ` [${inc.severity}]` : "";
    const status = inc.status ? ` (${inc.status})` : "";
    parts.push(`### ${inc.title}${severity}${status}`);
    if (inc.description) parts.push(inc.description);
    if (inc.source) parts.push(`Source: ${inc.source}`);
  }
  return parts;
}
